import React, { Component } from 'react';
import { Monitor } from '../../services/monitoring';
import { FeatureToggleServiceImpl } from '../services/FeatureToggleService';
import { TogglesMap } from '../types';
import { mergeConfigOverrides } from '../../factories/appConfig.factory';
import { defaultContext, Provider, ToggleContext } from './ToggleContext';

export interface ToggleContextProviderProps {
  monitorService: Monitor;
}

export class ToggleContextProvider extends Component<
  ToggleContextProviderProps,
  ToggleContext
> {
  public state = { ...defaultContext };

  private readonly featureToggleService =
    FeatureToggleServiceImpl.getInstance();

  constructor(props) {
    super(props);
    this.setupVariantProxy();
  }

  public componentDidMount() {
    this.initializeToggles();
  }

  private async initializeToggles(): Promise<void> {
    await this.fetchFeatureToggles();
    this.finishToggleInitialization();
  }

  private async fetchFeatureToggles(): Promise<void> {
    try {
      await this.featureToggleService.fetchTogglesFromContentful();
    } catch (error) {
      this.props.monitorService.reportError(error as Error);
    }
  }

  private initConfigToggles(): void {
    const configOverrides =
      this.featureToggleService.getToggleValue('configOverrides');
    mergeConfigOverrides(configOverrides);
  }

  private updateToggleState(
    toggles: ToggleContext['overridenTogglesCache'],
    callback?: () => void,
  ): void {
    this.setState(
      (prevState: Readonly<ToggleContext>) => ({
        ...prevState,
        overridenTogglesCache: {
          ...prevState.overridenTogglesCache,
          ...toggles,
        },
      }),
      callback,
    );
  }

  private getWindowVariants(): TogglesMap<unknown> {
    const localVariants: TogglesMap<unknown> = window.localVariants || {};
    const remoteVariants: TogglesMap<unknown> = window.variants || {};

    return Object.keys(localVariants).reduce(
      (accum: TogglesMap<unknown>, variantName: string) => {
        accum[variantName] = localVariants[variantName];
        return accum;
      },
      remoteVariants,
    );
  }

  private setupVariantProxy(): void {
    const windowVariants = this.getWindowVariants();

    window.variants = new Proxy(windowVariants, {
      set: (variants, variantName: string, value) => {
        windowVariants[variantName] = value;
        this.updateToggleState({ [variantName]: value });
        return true;
      },
      get: (
        variants: TogglesMap<unknown>,
        prop: string,
      ): ToggleValue<unknown> => {
        return windowVariants[prop];
      },
      ownKeys(target: TogglesMap<unknown>): ArrayLike<string | symbol> {
        return Object.keys(windowVariants);
      },
      has(target: TogglesMap<unknown>, key: string): boolean {
        return Object.keys(windowVariants).includes(key);
      },
    });
  }

  private finishToggleInitialization(): void {
    this.initConfigToggles();
    this.setState((prevState) => ({
      ...prevState,
      contentfulTogglesInitialized: true,
    }));
  }

  private handleUpdateToggleValue = (toggleName, toggleValue, options) => {
    if (options?.isLocallyPermanent) {
      this.featureToggleService.setLocalToggleValue(toggleName, {
        value: toggleValue,
      });
    }

    if (options?.isVariantToggle) {
      window.variants[toggleName] = toggleValue;
    } else {
      this.updateToggleState({ [toggleName]: toggleValue });
    }
  };

  private handleRemoveToggleValue = (toggleName, options) => {
    if (options?.isLocallyPermanent) {
      this.featureToggleService.removeLocalToggleValue(toggleName);
    }

    if (options?.isVariantToggle) {
      window.variants[toggleName] =
        this.featureToggleService.getToggleValue(toggleName);
    } else {
      this.updateToggleState({
        [toggleName]: this.featureToggleService.getToggleValue(toggleName),
      });
    }
  };

  private buildToggleContext(): ToggleContext {
    return {
      ...this.state,
      updateToggleValue: this.handleUpdateToggleValue,
      removeToggleValue: this.handleRemoveToggleValue,
      featureToggleService: this.featureToggleService,
    };
  }

  public render() {
    return (
      <Provider value={this.buildToggleContext()}>
        {this.props.children}
      </Provider>
    );
  }
}
